import ConnectedContainer from "components/container/ConnectedContainer.tsx";
import { useAppDispatch, useAppSelector } from "config/store";
import { UserType } from "globals/types/models.ts";
import { useCallback, useMemo, useState } from "react";
import Icon from "components/atom/Icon.tsx";
import { authActions } from "config/store/modules/auth.ts";
import { EnumPlanType, PlanTypeType } from "globals/types/globals.ts";
import Button from "components/atom/button/Button.tsx";
import ajax from "config/ajax.ts";
import { tryAsyncFunc } from "globals/functions/async.ts";
import TrialToPlanModal from "components/modals/TrialToPlanModal.tsx";
import PlanChangedModal from "components/modals/PlanChangedModal.tsx";
import { useMount } from "react-use";
import { Link } from "react-router-dom";
import { globalActions } from "config/store/modules/global.ts";
import { USER_PLAN_TYPES } from "globals/constants.ts";
import { useTranslation } from "react-i18next";

function MePlansPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentUser: UserType | null = useAppSelector(state => state.auth.user);
  //const totalEventCountCreated: number = useAppSelector(state => state.auth.totalEventCountCreated)


  const paymentMethods = useAppSelector(state => state.auth.paymentMethods);
  const [newSelectedPlan, setNewSelectedPlan] = useState<EnumPlanType | null>(null);
  const [isOpenTrialToPlan, setIsOpenTrialToPlan] = useState(false);
  const [isOpenPlanChanged, setIsOpenPlanChanged] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useMount(async () => {
    //  if (currentUser && currentUser.planType === USER_PLAN_TYPES.TRIAL) {
    //    dispatch(authActions.getMyEvents());//fetch event count so trial de-activate if necessary
    //  }
    dispatch(authActions.getMyPaymentMethods());
  });

  const changePlan = useCallback(async () => {
    setIsLoading(true);
    const [res, err] = await tryAsyncFunc(ajax.put(`/connected/me/plans`, {
      planType: newSelectedPlan
    }));


    if (err) {
      setIsLoading(false);
      return;
    }

    await dispatch(authActions.setUser(res.data.user));
    setIsLoading(false);
  }, [dispatch, newSelectedPlan]);

  const onAcceptTrialToPlan = useCallback(() => {
    changePlan();
    setIsOpenTrialToPlan(false);
  }, [changePlan]);

  const onAcceptPlanChanged = useCallback(() => {
    changePlan();
    setIsOpenPlanChanged(false);
  }, [changePlan]);

  const currentPlanType = useMemo(() => {
    if (!currentUser) return null;
    return currentUser.planType;
  }, [currentUser]);


  const plans = useMemo<PlanTypeType[]>(() => {
    const allPlans = [
      /*    {
           // active: currentPlanType === USER_PLAN_TYPES.TRIAL && totalEventCountCreated < 4,
           active: currentPlanType === USER_PLAN_TYPES.TRIAL,
           title: t('plans.titles.trial'),
           handle: USER_PLAN_TYPES.TRIAL,
           price: null,
           description: t('plans.descriptions.trial'),
           items: [
               {active: true, title: t('plans.items.publishEvent')},
               {active: true, title: t('plans.items.modifyFutureEvents')},
               {active: true, title: t('plans.items.recurringEvent')},
               {active: false, title: t('plans.items.modifyCurrentEvents')},
           ]
       },

    {
           active: true,
           title: t('plans.titles.unit'),
           handle: USER_PLAN_TYPES.UNIT,
            supPrice: t("plans.supPrices.unit"),
           price: t('plans.prices.unit'),
           description: t('plans.descriptions.unit'),
           items: [
               {active: true, title: t('plans.items.publishEvent')},
               {active: true, title: t('plans.items.modifyFutureEvents')},
               {active: false, title: t('plans.items.recurringEvent')},
               {active: false, title: t('plans.items.modifyCurrentEvents')},
               {active: false, title: t('plans.items.unlimitedEvents')},
           ]
       }, */
      {
        active: true,
        title: t("plans.titles.complete"),
        handle: USER_PLAN_TYPES.COMPLETE_MONTHLY,
        price: t("plans.prices.complete"),
        priceReduction: null,
        description: t("plans.descriptions.complete"),
        items: [
          { active: true, title: t("plans.items.freeTrial") },
          { active: true, title: t("plans.items.modifyFutureEvents") },
          { active: true, title: t("plans.items.recurringEvent") },
          { active: true, title: t("plans.items.modifyCurrentEvents") },
          { active: true, title: t("plans.items.unlimitedEvents") },
          { active: true, title: t("plans.items.optimizedDisplay") },
          { active: true, title: t("plans.items.automaticIntegration") }
        ]
      },
      {
        active: true,
        title: t("plans.titles.complete-yearly"),
        handle: USER_PLAN_TYPES.COMPLETE_YEARLY,
        price: t("plans.prices.complete-yearly"),
        priceReduction: t("plans.pricesReduction.complete-yearly"),
        description: t("plans.descriptions.complete-yearly"),
        items: [
          { active: true, title: t("plans.items.freeTrial") },
          { active: true, title: t("plans.items.modifyFutureEvents") },
          { active: true, title: t("plans.items.recurringEvent") },
          { active: true, title: t("plans.items.modifyCurrentEvents") },
          { active: true, title: t("plans.items.unlimitedEvents") },
          { active: true, title: t("plans.items.optimizedDisplay") },
          { active: true, title: t("plans.items.automaticIntegration") }
        ]
      }
    ];

    return allPlans.filter(p => p.active);
  }, [t]);


  const onPlanClicked = useCallback((type: EnumPlanType) => {
    return async () => {
      if ((type === USER_PLAN_TYPES.UNIT || type === USER_PLAN_TYPES.COMPLETE_MONTHLY || type === USER_PLAN_TYPES.COMPLETE_YEARLY) && paymentMethods.length === 0) {
        dispatch(globalActions.setToast({
          type: "warning",
          message: t("forms.errors.addPaymentMethod")
        }));
        return;
      }

      setNewSelectedPlan(type);

      //if the trial plan is showed, show the modal, if the trial is expired dont show modal
      const trialPlan = plans.find(p => p.handle === USER_PLAN_TYPES.TRIAL);
      if (trialPlan && trialPlan.active && currentPlanType === USER_PLAN_TYPES.TRIAL) {
        setIsOpenTrialToPlan(true);
        return;
      }

      setIsOpenPlanChanged(true);
    };
  }, [plans, currentPlanType, dispatch, paymentMethods.length, t]);


  if (!currentUser) {
    return null;
  }

  return (
    <ConnectedContainer title={t("plans.title")} isLoading={isLoading}>
      <div
        className={`mt-12 flex flex-row flex-wrap md:flex-nowrap justify-center items-stretch gap-12 md:gap-6 xl:gap-12`}>
        {plans.map(plan => (
          <div
            key={plan.handle}
            className={`w-full md:w-1/2 xl:w-[500px] flex flex-col gap-4 justify-start items-center rounded border p-4 ${currentPlanType === plan.handle ? "border-green bg-green-150" : "border-black"}`}>
            <div className="flex w-full flex-row items-center justify-between">
              <h3 className="text-lg font-medium">
                {plan.title}
              </h3>
              <div className="flex flex-col justify-end items-end">
                {plan.price && (
                  <h4 className={`text-sm lg:text-base font-medium ${plan.handle === "complete" ? "strike" : ""}`}
                      dangerouslySetInnerHTML={{ __html: plan.price }}
                  />
                )}
              </div>
            </div>
            {plan.priceReduction ? (
              <div
                className={`${currentPlanType === plan.handle ? "bg-white text-green-800" : "bg-green-100 text-green-800"} mr-auto px-5 py-2 rounded-full text-xs font-semibold inline-flex flex-row justify-center items-center gap-2`}>
                <Icon name="tag" className="w-5 h-5 min-w-5" />
                <span>{plan.priceReduction}</span>
              </div>
            ) : <div className="h-[36px]"/>}
            <p className="w-full text-gray-700 md:min-h-20"
               dangerouslySetInnerHTML={{ __html: plan.description }} />
            <div className="w-full pt-2">
              <h3 className="font-medium text-black">
                {t("plans.includes")}
              </h3>
              <ul className="pt-3">
                {plan.items.map((item, idx) => (
                  <li key={idx} className="flex flex-row items-center justify-start gap-2">
                    <Icon name={item.active ? "check" : "x"}
                          className={`block h-6 w-6 min-w-6 ${item.active ? "text-green-700" : "text-gray-500"}`} />
                    <span
                      className={`text-base ${item.active ? "text-green-700" : "text-gray-500"}`}>
                      {item.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full mt-auto pt-12">
              {plan.handle !== USER_PLAN_TYPES.TRIAL && paymentMethods.length === 0 && (
                <p className="text-xs text-center mx-auto inline-block w-full pb-2">
                  <Link className="underline cursor-pointer"
                        to="/profile/me/account#payment-methods">{t("forms.addPaymentMethod")}</Link>
                </p>
              )}
              <Button style="border" onClick={onPlanClicked(plan.handle)}
                      className={`w-full ${currentPlanType === plan.handle ? "pointer-events-none" : ""}`}>
                {currentPlanType === plan.handle ? t("plans.selected") : t("plans.select")}
              </Button>


            </div>
          </div>
        ))}
      </div>
      <div className="pt-6">
        <p className="text-xs text-center text-black">
          * {t("plans.plusTax")}
        </p>
      </div>
      {(currentPlanType === USER_PLAN_TYPES.COMPLETE_MONTHLY || currentPlanType === USER_PLAN_TYPES.COMPLETE_YEARLY || currentPlanType === USER_PLAN_TYPES.UNIT) && (
        <div className="flex flex-row items-center justify-center pt-12">
          <div>
            <Button style="red" onClick={onPlanClicked("canceled")} className="w-full">
              {t("plans.cancel")}
            </Button>
          </div>
        </div>
      )}

      <TrialToPlanModal isOpen={isOpenTrialToPlan}
                        onClose={() => setIsOpenTrialToPlan(false)}
                        onAccept={onAcceptTrialToPlan}

      />

      <PlanChangedModal isOpen={isOpenPlanChanged}
                        currentPlan={currentPlanType}
                        plan={newSelectedPlan}
                        onClose={() => setIsOpenPlanChanged(false)}
                        onAccept={onAcceptPlanChanged}

      />


    </ConnectedContainer>

  );
}

export default MePlansPage;
